import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { PaperPreview } from "@/components/docs/PaperPreview";
import { ResumeTemplate } from "@/components/docs/Templates";
import { API } from "@/lib/api";
import { exportPdf } from "@/lib/pdf";

export default function PublicResume() {
  const { shareId } = useParams();
  const [doc, setDoc] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/resumes/${shareId}`).then((r) => {
      setDoc(r.data);
      document.title = `${r.data.data?.name || "Resume"} | Pitchpaper`;
    }).catch(() => setMissing(true));
  }, [shareId]);

  const download = async () => {
    setBusy(true);
    try { await exportPdf("resume", { ...doc, title: doc.data?.name ? `${doc.data.name} resume` : doc.title }); }
    catch { toast.error("Could not create the PDF"); }
    finally { setBusy(false); }
  };

  return (
    <div className="paper-grain min-h-screen bg-paper-subtle" data-testid="public-resume-page">
      <header className="border-b border-paper-border bg-paper/85 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-4xl items-center gap-4 px-4">
          <Logo size={28} />
          {doc?.clean && (
            <button className="btn-thread ml-auto" onClick={download} disabled={busy} data-testid="public-download-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
            </button>
          )}
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-8 sm:py-12">
        {missing ? (
          <div className="mx-auto max-w-md rounded-2xl border border-paper-border bg-white p-10 text-center" data-testid="public-resume-missing">
            <h1 className="text-3xl text-ink">Link not available</h1>
            <p className="mt-2 text-ink-soft">The owner may have turned this link off.</p>
            <Link to="/" className="btn-thread mt-6">Visit Pitchpaper</Link>
          </div>
        ) : !doc ? <Loader2 className="mx-auto mt-20 h-6 w-6 animate-spin text-thread" /> : (
          <PaperPreview watermark={!doc.clean} testId="public-resume-preview" maxScale={1}>
            <ResumeTemplate template={doc.template} data={doc.data} accent={doc.accent} />
          </PaperPreview>
        )}
        <p className="mt-8 text-center font-mono text-xs text-taupe">Built with <Link to="/" className="text-thread hover:underline">Pitchpaper</Link> · <Link to="/terms" className="hover:underline" data-testid="public-terms-link">Terms</Link> · <Link to="/privacy" className="hover:underline" data-testid="public-privacy-link">Privacy</Link></p>
      </main>
    </div>
  );
}
